import {
  Box,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import useAuth, { setAuthStorage } from 'contexts/useAuth';
import { useState, useEffect } from 'react';
import { MdSwapHoriz, MdExpandMore } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import api, { getAccountsAccess, changeUserAccount } from 'services/api.service';
import { maskAccountNumber } from 'utils/account';

interface AccountAccess {
  customerAccess: {
    id: string;
    name: string;
    email: string;
    accountNumber: string;
  };
}

export default function AccountSwitcher() {
  const { userData, updateUserData } = useAuth();
  const toast = useToast();
  const history = useHistory();
  const [accounts, setAccounts] = useState<AccountAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);

  const textColor = useColorModeValue('gray.200', 'gray.200');
  const menuBg = useColorModeValue('brand.900', 'navy.800');
  const hoverBg = useColorModeValue('brand.700', 'navy.700');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getAccountsAccess();
      setAccounts(data);
    } catch (error) {
      // Silently fail - user may not have access to multiple accounts
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchAccount = async (accountId: string) => {
    try {
      setLoadingAccount(true);
      const response = await changeUserAccount(accountId);

      // 1. Update localStorage with new token
      localStorage.setItem('Authorization', response.token);

      // 2. Update axios default headers with new token
      api.defaults.headers.Authorization = 'Bearer ' + response.token;

      // 3. Update auth storage with new user data
      const newUserData = {
        authorization: response.token,
        email: response.email,
        isAuth: true,
        id: response.id,
        name: response.name,
        balance: response.balance,
        amountLocked: response.amountLocked,
        balancePending: response.balancePending,
        features: response.features,
        accountNumber: response.accountNumber,
        on: DateTime.now().toISO(),
      };
      setAuthStorage(newUserData);

      // 4. Invalidate all queries to refetch with new account data
      await queryClient.invalidateQueries();

      // 5. Update user data in context
      updateUserData();

      toast({
        title: 'Sucesso',
        description: `Conta alterada para ${response.name}`,
        status: 'success',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });

      // 6. Redirect to dashboard
      history.push('/access/dashboard');
      
      // 7. Force page reload to ensure all components are updated
      window.location.reload();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao trocar de conta',
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setLoadingAccount(false);
    }
  };

  // Don't show if no accounts available
  if (!loading && accounts.length === 0) {
    return null;
  }

  return (
    <Box w="100%" px={4} mb={4}>
      <Menu>
        <MenuButton
          as={Button}
          w="100%"
          variant="ghost"
          color={textColor}
          rightIcon={loading ? <Spinner size="sm" /> : <Icon as={MdExpandMore} />}
          leftIcon={<Icon as={MdSwapHoriz} />}
          _hover={{ bg: hoverBg }}
          _active={{ bg: hoverBg }}
          isDisabled={loading || loadingAccount}
          size="sm"
        >
          <VStack spacing={0} align="start" flex={1}>
            <Text fontSize="xs" opacity={0.7}>
              Trocar de conta
            </Text>
            <Text fontSize="sm" fontWeight="bold" isTruncated maxW="150px">
              {userData?.name || 'Carregando...'}
            </Text>
          </VStack>
        </MenuButton>
        <MenuList bg={menuBg} borderColor="whiteAlpha.300" minW="280px" maxW="320px">
          <Box px={3} py={2} borderBottomWidth={1} borderColor="whiteAlpha.300">
            <Text fontSize="xs" color={textColor} opacity={0.7} fontWeight="semibold">
              Contas disponíveis
            </Text>
          </Box>
          {accounts.map((account) => (
            <MenuItem
              key={account.customerAccess.id}
              onClick={() => handleSwitchAccount(account.customerAccess.id)}
              bg={menuBg}
              _hover={{ bg: hoverBg }}
              _focus={{ bg: hoverBg }}
              isDisabled={loadingAccount}
              py={3}
              px={3}
            >
              <HStack w="100%" justify="space-between" spacing={3}>
                <VStack align="start" spacing={1} flex={1} minW={0}>
                  <Text 
                    color={textColor} 
                    fontSize="sm" 
                    fontWeight="bold"
                    isTruncated
                    w="100%"
                  >
                    {account.customerAccess.name}
                  </Text>
                  <Text 
                    color={textColor} 
                    fontSize="xs" 
                    opacity={0.7}
                    isTruncated
                    w="100%"
                  >
                    {account.customerAccess.email}
                  </Text>
                  <Text 
                    color={textColor} 
                    fontSize="xs" 
                    opacity={0.8}
                    fontWeight="medium"
                  >
                    Conta: {maskAccountNumber(account.customerAccess.accountNumber)}
                  </Text>
                </VStack>
                {loadingAccount && <Spinner size="xs" color={textColor} />}
              </HStack>
            </MenuItem>
          ))}
          {accounts.length === 0 && !loading && (
            <Box px={3} py={2}>
              <Text fontSize="sm" color={textColor} opacity={0.7}>
                Nenhuma conta disponível
              </Text>
            </Box>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
}

